import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button, ModeToggle, cn, useThemeStore, defaultThemeIdForPersona } from '@kidshome/ui';
import {
  useAuthActions,
  useAuthStore,
  resetBootstrap,
  useCan,
  useFeature,
} from '@/entities/session';
import type { Persona } from '@/entities/session';
import { Avatar, LanguageToggle, PortalHostContext } from '@/shared/ui';
import { InstallAffordance, clearApiCache } from '@/shared/pwa';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { usePushNotificationInvalidation } from '@/features/notifications/use-push-invalidation';
import { Perm } from '@/shared/lib/permission-codes';
import { Feature } from '@/shared/lib/features';
import { useInbox } from '@/widgets/inbox';
import { PausedRewardsBanner } from '@/widgets/paused-rewards';
import { ActingAsBanner } from '@/widgets/acting-as';
import { BottomNav, type NavItem } from '@/widgets/app-shell/BottomNav';
import { HeroThemeApplier } from '@/widgets/app-shell/HeroThemeApplier';
import {
  HomeIcon,
  HabitsIcon,
  LearnIcon,
  RewardsIcon,
  ProfileIcon,
  HeroIcon,
  CalendarIcon,
  FamilyIcon,
  ApprovalsIcon,
  HistoryIcon,
  ConsoleIcon,
  LogoutIcon,
} from '@/widgets/app-shell/nav-icons';

/**
 * The persona shell (FSD `widgets`): a soft Cozy-Pastel header (app name + member avatar + theme
 * toggle + logout; the text affordances collapse to labelled icon buttons below `sm` so the row
 * fits a 360px phone) over a routed content outlet and a persona bottom nav. The kid shell shows
 * Home (the child's day) · Habits · Learn · Rewards · Profile — max five tabs so the bar fits small
 * phones; the Shop/Customize surface is reached from the Hero page. The
 * family shell shows Home · Calendar · Rewards · Family, with an Inbox tab added only when the user
 * can approve (authz-aware UI; the server still enforces).
 */
export function AppShell({ persona }: { persona: Persona }): React.ReactElement {
  const { t } = useTranslation(['family', 'common', 'settings', 'auth', 'learning']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout } = useAuthActions();
  const user = useAuthStore((s) => s.user);
  const { can } = useCan();
  const hasFeature = useFeature();
  // Refresh the bell/center immediately when a push arrives with the app open (ADR-0135 §8).
  usePushNotificationInvalidation();
  const applyScopedMode = useThemeStore((s) => s.applyScopedMode);
  const clearScopedMode = useThemeStore((s) => s.clearScopedMode);
  const applyScopedTheme = useThemeStore((s) => s.applyScopedTheme);
  const clearScopedTheme = useThemeStore((s) => s.clearScopedTheme);
  // The in-scope portal host (mounted at the shell root below). Overlays (ActionSheet/Dialog/onboarding)
  // portal into it so they inherit this shell's theme scope instead of `document.body`/the OS scheme.
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  // The kid shell root — the hero theme is applied HERE (not `document`), so a hero's `--sky-*` overrides win
  // over the `.kid` stylesheet defaults and the palette stays scoped to the kid subtree (ADR-0102).
  const shellRef = useRef<HTMLDivElement>(null);

  // Each shell SCOPES its persona theme while mounted (mirrors how AdminShell scopes command-center):
  // the kid persona renders cozy-pastel, the family/relative persona renders warm-neutral-teal. Scoping
  // (non-persisting) makes the rendered theme deterministic regardless of the user's persisted/legacy
  // `themeId`, so the family UI is ALWAYS warm-neutral+teal (its sole theme) and the kid UI always pastel.
  // The kid experience is also light-first by design (its bespoke `.kid` surfaces are hardcoded light), so
  // the kid persona additionally PINS light mode — otherwise an OS-dark token set clashes dark-on-dark. The
  // family/relative persona keeps the user's chosen mode (light/dark/system). Cleared on unmount.
  useEffect(() => {
    applyScopedTheme(defaultThemeIdForPersona(persona === 'kid' ? 'kid' : 'family'));
    if (persona === 'kid') {
      applyScopedMode('light');
    } else {
      clearScopedMode();
    }
    return () => {
      clearScopedTheme();
      clearScopedMode();
    };
  }, [persona, applyScopedTheme, clearScopedTheme, applyScopedMode, clearScopedMode]);
  // The unified Inbox count (chore approvals + pending reward redemptions). A child is never a
  // guardian, so `useInbox` resolves both gates false and fetches nothing for the kid persona.
  const inbox = useInbox();
  const inboxCount = inbox.totalPending;
  // The Inbox tab is relevant only when a queue the guardian can act on is ALSO an enabled feature:
  // chore approvals need Chores; reward decisions/approvals need Economy. With both off the unified
  // queue is empty, so the guardian gets the Family overview tab instead (ADR-0076).
  const inboxRelevant =
    (inbox.canApprove && hasFeature(Feature.Chores)) ||
    ((inbox.canDecide || inbox.canApproveRewards) && hasFeature(Feature.Economy));

  const items = useMemo<NavItem[]>(() => {
    if (persona === 'kid') {
      // Max FIVE tabs so the bar always fits a 360px phone (the primary kid device class). Home IS
      // the child's day (chores/today), so there is no separate Tasks tab; the Shop/Customize
      // surface is reached from the Hero page.
      const kidItems: NavItem[] = [
        { to: '/', label: t('family:nav.home'), icon: <HomeIcon />, end: true },
      ];
      // The kid Habits tab — only when the child can check their own habits AND the household runs the
      // Habits module (the API self-scopes + enforces; this just hides an affordance a viewer can't use).
      if (can(Perm.HabitCheckOwn) && hasFeature(Feature.Habits)) {
        kidItems.push({
          to: '/habits',
          label: t('family:nav.habits'),
          icon: <HabitsIcon />,
        });
      }
      // The Learn tab — the educational-games play loop (ADR-0027). Every child holds `learning:read`
      // (the learning reads/writes gate on it), so it shows for any kid when Learning is enabled.
      if (can(Perm.LearningRead) && hasFeature(Feature.Learning)) {
        kidItems.push({
          to: '/learn',
          label: t('learning:nav.learn'),
          icon: <LearnIcon />,
        });
      }
      // Rewards is the spend surface of the coin economy — hidden when the Economy module is off.
      if (hasFeature(Feature.Economy)) {
        kidItems.push({
          to: '/rewards',
          label: t('family:nav.rewards'),
          icon: <RewardsIcon />,
        });
      }
      // Hero (ADR-0095) REPLACES the standalone Profile tab when Play is on — the former profile folds
      // into the Hero page's collapsible "Me" section (nothing is lost, and `/profile` still aliases to
      // it). With Play off there is no hero surface, so we keep the plain Profile tab so the tab is never
      // lost. The five-tab budget holds either way.
      if (hasFeature(Feature.Play)) {
        kidItems.push({
          to: '/hero',
          label: t('family:nav.hero'),
          icon: <HeroIcon />,
        });
      } else {
        kidItems.push({
          to: '/profile',
          label: t('family:nav.profile'),
          icon: <ProfileIcon />,
        });
      }
      return kidItems;
    }
    const base: NavItem[] = [
      { to: '/', label: t('family:nav.home'), icon: <HomeIcon />, end: true },
    ];
    // Calendar surfaces the chore schedule; Rewards surfaces the coin economy — each hidden with its
    // module (ADR-0076).
    if (hasFeature(Feature.Chores)) {
      base.push({
        to: '/calendar',
        label: t('family:nav.calendar'),
        icon: <CalendarIcon />,
      });
    }
    if (hasFeature(Feature.Economy)) {
      base.push({
        to: '/rewards',
        label: t('family:nav.rewards'),
        icon: <RewardsIcon />,
      });
    }
    // A guardian who can act on a relevant, enabled queue gets the Inbox tab (the unified decisions
    // surface, with a count badge); otherwise they get the "Family" overview tab instead.
    if (inboxRelevant) {
      base.push({
        to: '/inbox',
        label: t('family:nav.inbox'),
        icon: <ApprovalsIcon />,
        badge: inboxCount,
        badgeLabel: t('family:inbox.awaiting', { count: inboxCount }),
      });
    } else {
      base.push({
        to: '/family',
        label: t('family:nav.family'),
        icon: <FamilyIcon />,
      });
    }
    // A guardian who can read any child's ledger gets the History tab — only with the Economy module on.
    if (can(Perm.LedgerReadAny) && hasFeature(Feature.Economy)) {
      base.push({
        to: '/history',
        label: t('family:nav.history'),
        icon: <HistoryIcon />,
      });
    }
    return base;
  }, [persona, t, can, hasFeature, inboxRelevant, inboxCount]);

  const onLogout = async (): Promise<void> => {
    await logout();
    resetBootstrap();
    // Clear the TanStack cache so the next account on this shared tab (kid-login "Who's playing?")
    // can't see the previous user's cached balances/members.
    queryClient.clear();
    // Also purge the service-worker runtime API cache (keyed by URL only) so the next account on a
    // shared device can't be served the previous user's cached reads offline / on a SW timeout.
    clearApiCache();
    void navigate('/login', { replace: true });
  };

  return (
    <div
      ref={shellRef}
      className={cn('flex min-h-dvh flex-col bg-bg text-text', persona === 'kid' && 'kid')}
    >
      {/* The chosen hero recolours the kid shell from its own palette (ADR-0099/0102) — headless, kid persona
          only, applied to THIS `.kid` root (so the hero's `--sky-*` win over the `.kid` defaults, and the
          palette never leaks into the family/admin shell), cleared on logout. */}
      {persona === 'kid' && <HeroThemeApplier targetRef={shellRef} />}
      <PortalHostContext.Provider value={portalHost}>
        {/* Below `sm` the text affordances (Parent console / Log out) collapse to labelled icon
          buttons so the header row fits a 360px phone without wrapping or clipping. */}
        <header
          className={cn(
            // Pad the top by the device safe-area inset so the header never sits under the status bar /
            // notch when the PWA runs standalone (the kid's primary device class) — mirrors the bottom
            // nav's `safe-area-inset-bottom`, which was already handled.
            'flex items-center justify-between gap-2 px-3 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top,0px))] sm:px-5',
            // Kid: float the header in the warm `.kid` gradient canvas, matching the approved kid kit's
            // borderless/transparent appbar — the family chrome's opaque surface + hard divider clashed with
            // the dreamy canvas. Family/admin keep the opaque, bordered app bar (their designed chrome).
            persona === 'kid' ? 'bg-transparent' : 'border-b border-border bg-surface',
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            {/* The header avatar is the one tap to the signed-in member's self surface — the kid's
                profile ("Я", appearance/settings) and the guardian's Account (security/appearance/
                notifications). There is no separate "Account" text link; the avatar IS the affordance,
                consistent across every shell. */}
            {user && (
              <Link
                to={persona === 'kid' ? '/profile' : '/account'}
                aria-label={persona === 'kid' ? t('family:nav.profile') : t('auth:account.link')}
                className="shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                <Avatar avatar={user.avatar} size="sm" />
              </Link>
            )}
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate font-display text-md font-bold text-primary">
                {t('common:app.name')}
              </span>
              {user && <span className="truncate text-xs text-text-muted">{user.displayName}</span>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {/* The guardian's Account hub is reached by tapping the header avatar (above) — no separate
              "Account" link. The kid signs in by PIN device-claim (no password), so the kid self surface
              is `/profile`, also reached from the avatar. */}
            {persona === 'family' && (
              <Link
                to="/admin"
                aria-label={t('common:nav.console')}
                className="flex items-center rounded-md px-2 py-1.5 text-sm font-medium text-text-muted outline-none transition-colors hover:bg-surface-muted hover:text-text focus-visible:ring-2 focus-visible:ring-ring sm:px-2.5"
              >
                <span className="sm:hidden" aria-hidden="true">
                  <ConsoleIcon />
                </span>
                <span className="hidden whitespace-nowrap sm:inline">
                  {t('common:nav.console')}
                </span>
              </Link>
            )}
            {/* Informational notification bell (ADR-0135) — distinct from the actionable Inbox tab
              badge; hidden when the `notifications` feature is off. Kid-sized tap target on the kid
              persona. */}
            <NotificationBell emphasizeTarget={persona === 'kid'} />
            <InstallAffordance />
            <LanguageToggle className="hidden sm:inline-flex" />
            {/* The kid persona pins light mode (it has no dark design), so a mode toggle would be a dead
              control there — show it only for the family shell, whose dark mode is real. */}
            {persona === 'family' && <ModeToggle label={t('settings:appearance.toggleMode')} />}
            <Button
              variant="ghost"
              size="sm"
              // On the kid persona this is the only always-present header control and a child taps it with
              // small fingers — give it a ≥44px target (the kid kit's own min), not the 32px `sm` height.
              className={cn(persona === 'kid' && 'min-h-11 min-w-11')}
              onClick={() => void onLogout()}
              aria-label={t('common:nav.logout')}
            >
              <span className="sm:hidden" aria-hidden="true">
                <LogoutIcon />
              </span>
              <span className="hidden whitespace-nowrap sm:inline">{t('common:nav.logout')}</span>
            </Button>
          </div>
        </header>
        {/* Persistent platform-owner impersonation banner (ADR-0055) — always visible while acting in
          another household, on any persona screen; Exit returns to the platform list as the owner. */}
        <ActingAsBanner />
        {/* The household-wide paused-rewards strip — guardians + relatives only (a kid sees a held reward
          locked in their own shop; surfacing it household-wide would shame rather than coordinate). */}
        {persona === 'family' && <PausedRewardsBanner />}
        {/* Family pages are a mobile-first single column; centre them in a readable max-width column so
            on desktop the header, sub-nav and page content all align (not full-bleed / left-shifted).
            The kid persona keeps its own `.kid-page` width handling, so it stays unconstrained here. */}
        <main className="flex-1 overflow-y-auto p-4">
          <div className={cn('mx-auto w-full', persona === 'family' && 'max-w-2xl')}>
            <Outlet />
          </div>
        </main>
        <BottomNav items={items} />
        {/* In-scope portal host: modal overlays mount here (a child of this themed root) so they inherit the
          shell's bespoke vars + resolved `--color-*` mode instead of `document.body`/the OS scheme. It is
          an empty, layout-free node — its overlays are `position: fixed`, so placement does not matter. */}
        <div ref={setPortalHost} />
      </PortalHostContext.Provider>
    </div>
  );
}
